/* eslint-disable @typescript-eslint/no-loop-func */
import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Card from '@/components/Card/Card';
import Icon from '@/components/Icons/Icon';
import ConfirmationModal from '@/components/Modal/ConfirmationModal/ConfirmationModal';
import ProgressIndicator from '@/components/ProgressIndicator/ProgressIndicator';
import { ProgressOptions } from '@/components/StatusTracker/StatusTracker.types';
import Tag from '@/components/Tag/Tag';
import { TagTypes } from '@/components/Tag/Tag.types';
import Tooltip from '@/components/Tooltip/Tooltip';
import { PositionTypes } from '@/components/Tooltip/Tooltip.types';
import { Freeze, FreezeType } from '@/types/freeze.types';
import { NationalProgramStates } from '@/types/nationalProgram.types';
import useConfirmationModal from '@/utils/useConfirmationModal/useConfirmationModal';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import EditFreeze from '../../EditFreeze/EditFreeze';
import DeleteConfirmationModal from './DeleteConfirmationModal/DeleteConfirmationModal';
import styles from './FreezeCard.module.css';

const FreezeCard: FC<Freeze> = ({
  id,
  bundleId,
  name,
  optInStartDate,
  startDate,
  endDate,
  nationalDefaultPrice,
  nationalPricePoints,
  optOutHidden,
  freezeType,
  state,
  optInProgressPercent,
  bundleData,
  redeemableOnline,
  visibleOnline,
  sustainPricingFlag,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isOptInOrLive = [NationalProgramStates.LIVE, NationalProgramStates.OPT_IN].includes(state);

  const [confirmationModalVisible, setConfirmationModalVisible] = useState(false);
  const [showEditWizard, setShowEditWizard] = useState(false);
  const openEditWizard = () => setShowEditWizard(true);
  const closeEditWizard = () => setShowEditWizard(false);

  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
  const openDeleteModal = () => setDeleteConfirmationModalOpen(true);
  const closeDeleteModal = () => setDeleteConfirmationModalOpen(false);
  const { open } = useConfirmationModal();

  const redeemableValue = redeemableOnline ? 'Yes' : 'No';
  const visibleValue = visibleOnline ? 'Yes' : 'No';
  const optOutHiddenValue = optOutHidden ? 'Hide' : 'Show';
  const pricingOnEndValue = sustainPricingFlag ? t('freezeCard.sustain') : t('freezeCard.revert');

  const roundToNearestTwo = (value: number | null) => {
    if (value === null) return value;
    if (value > 0 && value < 0.01) return 0.01;
    if (Number.isInteger(value)) return +value.toFixed(0);
    return +value.toFixed(2);
  };

  return (
    <Card className={classNames(styles.card, { [styles.live]: isOptInOrLive })}>
      {isOptInOrLive && (
        <div className={styles.countdown}>
          {state === NationalProgramStates.LIVE ? t('common.live') : t('common.optInLive')}
        </div>
      )}
      <div className={styles.header}>
        <span className={styles.freezeName}>{name}</span>
        <span className={styles.freezeType}>&nbsp;- {t(`freezeCard.types.${freezeType}`)}</span>
      </div>
      <div
        className={classNames(styles.dateProgramDetails, {
          [styles.withBorder]: freezeType !== FreezeType.MAINTENANCE,
        })}
      >
        {freezeType !== FreezeType.MAINTENANCE && (
          <div className={styles.column}>
            <div className={styles.row}>
              <span className={styles.dateLabel}>{t('freezeCard.optInDate')}:</span>
              <span>{DateTime.fromISO(optInStartDate, { setZone: true }).toFormat('D')}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.dateLabel}>
                {t('freezeCard.optInProgress')}:
                <Tooltip content={t('freezeCard.optInProgressTooltip')} position={PositionTypes.Top} />
              </span>
              {isOptInOrLive ? (
                <span className={styles.progress}>
                  {optInProgressPercent !== undefined || optInProgressPercent !== null
                    ? `${roundToNearestTwo(optInProgressPercent)}%`
                    : '0%'}
                  <Button
                    variant={ButtonVariants.Text}
                    onClick={() => navigate(`/bundles/${bundleId}/nationalprograms/${id}`)}
                  >
                    {t('common.viewDetailsButton')}
                  </Button>
                </span>
              ) : (
                <span>--</span>
              )}
            </div>

            {isOptInOrLive && (
              <div className={styles.progressTooltip}>
                <ProgressIndicator
                  progress={
                    optInProgressPercent !== undefined
                      ? (roundToNearestTwo(optInProgressPercent) as ProgressOptions)
                      : 0
                  }
                />
              </div>
            )}
          </div>
        )}
        <div className={styles.column}>
          <div className={styles.row}>
            <span className={styles.dateLabel}>{t('common.startDate')}:</span>
            <span>{DateTime.fromISO(startDate, { setZone: true }).toFormat('D')}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.dateLabel}>{t('common.endDate')}:</span>
            <span>{DateTime.fromISO(endDate, { setZone: true }).toFormat('D')}</span>
          </div>
        </div>

        <div className={styles.column}>
          <div className={styles.row}>
            <span className={styles.dateLabel}>{t('nationalProgramDetails.redeemableOnline')}:</span>
            <span>{redeemableValue}</span>
          </div>
          <div className={styles.row}>
            <span className={styles.dateLabel}>{t('nationalProgramDetails.visibleOnline')}:</span>
            <span>{visibleValue}</span>
          </div>
        </div>

        <div className={styles.column}>
          <div className={styles.row}>
            <span className={styles.dateLabel}>{t('nationalProgramDetails.OptOutSetting')}:</span>
            <span>{optOutHiddenValue}</span>
          </div>
          {freezeType === FreezeType.NATIONAL_PROGRAM && (
            <div className={styles.row}>
              <span className={styles.dateLabel}>{t('freezeCard.pricingOnEnd')}:</span>
              <span className={styles.pricingOnEndValue}>
                <Tag label={pricingOnEndValue} type={sustainPricingFlag ? TagTypes.Scheduled : TagTypes.Inactive} />
              </span>
            </div>
          )}
        </div>
      </div>
      {freezeType !== FreezeType.MAINTENANCE && (
        <>
          <div className={styles.heading}>{t('freezeCard.availablePricePoints')}</div>
          <div className={styles.programDetails}>
            <div className={styles.pricePointColumn}>
              <div className={styles.row}>
                <span className={styles.dateLabel}>{t('freezeCard.defaultPrice')}:</span>
                <span>${nationalDefaultPrice?.toFixed(2)}</span>
              </div>
              {nationalPricePoints.slice(0, 1).map((price, index) => (
                <div className={styles.row} key={price}>
                  <span className={styles.dateLabel}>{t('freezeCard.pricePoint', { number: index + 2 })}:</span>
                  <span>{price ? price.toFixed(2) : '-'}</span>
                </div>
              ))}
            </div>
            <div className={styles.pricePointColumn}>
              {nationalPricePoints.slice(1, 3).map((price, index) => (
                <div className={styles.row} key={price}>
                  <span className={styles.dateLabel}>{t('freezeCard.pricePoint', { number: index + 3 })}:</span>
                  <span>{price ? price.toFixed(2) : '-'}</span>
                </div>
              ))}
            </div>
            {/* HAG is out of scope */}
            {/* <div className={styles.column}>
              <div className={styles.row}>
                <span className={styles.dateLabel}>Hawaii/Alaska/Guam Default:</span>
                <span>${hagDefaultPrice?.toFixed(2)}</span>
              </div>
              {hagPricePoints.map((price, index) => (
                <div className={styles.row} key={price}>
                  <span className={styles.dateLabel}>{`Hawaii/Alaska/Guam Price ${index + 1}:`}</span>
                  <span>${price.toFixed(2)}</span>
                </div>
              ))}
            </div> */}
          </div>
        </>
      )}
      <div className={styles.buttons}>
        <Button variant={ButtonVariants.Text} className={styles.editButton} onClick={openEditWizard}>
          <Icon iconName="Edit2" />
          {t('freezeCard.editFreezeButton')}
        </Button>
        <Button variant={ButtonVariants.Text} className={styles.deleteButton} onClick={openDeleteModal}>
          <Icon iconName="Trash" />
          {t('freezeCard.deleteButton')}
        </Button>
      </div>
      <EditFreeze
        bundleData={bundleData}
        freezeId={id}
        name={name}
        optInStartDate={optInStartDate}
        startDate={startDate}
        endDate={endDate}
        freezeType={freezeType}
        nationalDefaultPrice={nationalDefaultPrice}
        nationalPricePoints={nationalPricePoints}
        optOutHidden={optOutHidden}
        state={state}
        isOpen={showEditWizard}
        handleClose={closeEditWizard}
        redeemableOnline={redeemableOnline}
        visibleOnline={visibleOnline}
        sustainPricingFlag={sustainPricingFlag}
        setConfirmationModalVisible={setConfirmationModalVisible}
      />
      <ConfirmationModal
        title={t('freezeCard.editFreezeConfirmTitle', { name })}
        buttonText={t('freezeCard.backToBundleButton')}
        isOpen={confirmationModalVisible}
        hideStatusUpdateText={freezeType === FreezeType.NATIONAL_PROGRAM || freezeType === FreezeType.MAINTENANCE}
        onClose={() => setConfirmationModalVisible(false)}
      />
      <DeleteConfirmationModal
        isOpen={deleteConfirmationModalOpen}
        onClose={closeDeleteModal}
        freezeType={freezeType}
        freezeName={name}
        startDate={startDate}
        endDate={endDate}
        idToDelete={id}
        openConfirmationModal={open}
      />
    </Card>
  );
};

export default FreezeCard;
